import TextInput from 'components/inputs/TextInput/TextInput';
import Select from 'react-select';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useHttp from 'utils/hooks/useHttp';
import './StockCreateNew.scss';

import Spinner from 'components/spinner/Spinner';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { notify } from 'utils/marketplace';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';

import { useDeviceDetect } from 'utils/hooks/deviceDetect';
import { IDataSuppliersValue, IValidateStock } from 'models/stock';

type Props = {
  setOpenModalCreate: (e: boolean) => void;
  setSubmit: (e: boolean) => void;
};

const StockCreateNew: React.FC<Props> = (props) => {
  let isMounted = true;
  const { t } = useTranslation();
  const http = useHttp();
  const { isMobile, isTablet } = useDeviceDetect();
  const [suppliers, setSuppliers] = useState<IDataSuppliersValue[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingModal, setLoadingModal] = useState<boolean>(true);

  const validate = Yup.object().shape({
    reference: Yup.string()
      .trim()
      .required(t('_fild_required'))
      .matches(/^[a-zA-Z0-9]+$/, 'No special characters'),
    quantity: Yup.number()
      .transform((value, originalValue) => (/\s/.test(originalValue) ? NaN : value))
      .required(t('_fild_required'))
      .typeError(t('_must_number')),
    data_supplier: Yup.object().shape({
      label: Yup.string().required(t('_fild_required')),
      value: Yup.string().required(t('_fild_required')),
    }),
    price_eur: Yup.number()
      .transform((value, originalValue) => (/\s/.test(originalValue) ? NaN : value))
      .required(t('_fild_required'))
      .typeError(t('_must_number')),
    price_gbp: Yup.number()
      .transform((value, originalValue) => (/\s/.test(originalValue) ? NaN : value))
      .required(t('_fild_required'))
      .typeError(t('_must_number')),
  });

  const {
    getValues,
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IValidateStock>({ resolver: yupResolver(validate) });

  useEffect(() => {
    if (isMounted) {
      setLoadingModal(true);

      http.stockHttp
        .getSupplierTypes()
        .then((res) => {
          const setSuppliersData = res.data[location.pathname.substring(location.pathname.lastIndexOf('/') + 1)].map((item) => {
            return { value: item.id, label: item.name };
          });

          setSuppliers(setSuppliersData);
          setLoadingModal(false);
        })
        .catch(() => {
          setLoadingModal(false);
        });
    }

    return () => {
      isMounted = false;
    };
  }, []);

  const customStyles = (hasError) => ({
    control: (styles) => ({
      ...styles,
      ...(hasError ? { borderColor: 'red' } : { border: 'none' }),
    }),
  });

  const onSubmit = async () => {
    setLoading(true);
    const data = getValues();

    const params = {
      quantity: data.quantity,
      reference: data.reference,
      type_supplier: location.pathname.substring(location.pathname.lastIndexOf('/') + 1),
      data_supplier: data.data_supplier?.value,
      priceEur: Number(data.price_eur),
      priceGbp: Number(data.price_gbp),
    };

    http.stockHttp
      .createStock(params)
      .then(() => {
        notify(t('_stock_created'), 'success', t('_success'));
        props.setOpenModalCreate(false);
        props.setSubmit(true);
      })
      .catch(() => {
        notify('error', t('_error'));
        setLoading(false);
      });
  };

  return (
    <div className="stock-create-new-container">
      <div
        className="close-modal"
        onClick={() => {
          props.setOpenModalCreate(false);
        }}
      >
        <FontAwesomeIcon icon={faClose} />
      </div>
      <div className="title">{t('_create_stock')}</div>
      {loadingModal ? (
        <div className="spinner-info">
          {' '}
          <Spinner />{' '}
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="stock-create-body">
            <div className="row">
              <div className="container">
                <label>{t('_stock_supplier')}</label>
                <Controller
                  control={control}
                  name="data_supplier"
                  render={({ field: { onChange, value } }) => (
                    <Select
                      value={value}
                      className="select-dropdown"
                      styles={customStyles(errors.data_supplier?.label?.type)}
                      onChange={(val) => {
                        onChange(val);
                      }}
                      options={suppliers}
                      placeholder={`${t('_select')}  ${t('_stock_supplier')}`}
                      isSearchable={!isMobile && !isTablet}
                    />
                  )}
                />
                <p className="error">{errors.data_supplier?.label?.type == 'required' ? errors.data_supplier?.label?.message : ''}</p>
              </div>

              <div className={`container ${errors.reference?.type == 'required' || errors.reference?.type == 'matches' ? 'error' : ''}`}>
                <label>{t('_stock_reference')}</label>
                <TextInput {...register('reference')} />
                <p className="error-message">
                  {errors.reference?.type == 'required' || errors.reference?.type == 'matches' ? errors.reference?.message : ''}
                </p>
              </div>
              <div className={`container ${errors.quantity?.type ? 'error' : ''}`}>
                <label>{t('_stock_quantity')}</label>
                <TextInput {...register('quantity')} />
                <p className="error-message">{errors.quantity?.type ? errors.quantity?.message : ''}</p>
              </div>
            </div>
            <div className="row">
              <div className={`container ${errors.price_eur?.type ? 'error' : ''}`}>

                <label>{t('_price')} EUR</label>
                <TextInput {...register('price_eur')} />

                <p className="error-message">{errors.price_eur?.type ? errors.price_eur?.message : ''}</p>
              </div>

              <div className={`container ${errors.price_gbp?.type ? 'error' : ''}`}>
                <label>{t('_price')} GBP</label>
                <TextInput {...register('price_gbp')} />

                <p className="error-message">{errors.price_gbp?.type ? errors.price_gbp?.message : ''}</p>
              </div>
            </div>
          </div>
          <div className="btn-save">
            <button type="submit">
              {' '}
              {loading ? <Spinner class="extra-small" /> : null} <div className="title-btn-save">{t('_stock_create_article')}</div>
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default StockCreateNew;
